import React, { FunctionComponent } from 'react';

import { List, ListItem, Text } from 'components/chroma';

import { toAussieStandardDateFormat } from 'scenes/common/helpers/datetime';

import {
  MotorFullPageProps,
  SpecialDetail,
  MotorQuoteSubSection,
  MotorPolicySubSection,
  MotorSection,
} from 'scenes/motor/types';
import {
  getAdditionalDrivers,
  getSpecialDetails,
  isComprehensiveSelected,
  isCoverUnder25Included,
  isUnder25CoverSelected,
} from 'scenes/motor/selectors';
import SummaryCard from 'components/SummaryCard';

export const AdditionalDriverAndUnder25Summary: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditQuoteDetails, onEditPolicyDetails }) => {
  const additionalDrivers = getAdditionalDrivers(funnelState);
  const driverSpecialDetail = {
    LicenceCancelled: getSpecialDetails(funnelState).find(
      (sd) => sd === SpecialDetail.LicenceCancelled
    ),
    InsuranceClaimRefused: getSpecialDetails(funnelState).find(
      (sd) => sd === SpecialDetail.InsuranceClaimRefused
    ),
    InsurancePolicyCancelled: getSpecialDetails(funnelState).find(
      (sd) => sd === SpecialDetail.InsurancePolicyCancelled
    ),
    DeniedPurchase: getSpecialDetails(funnelState).find(
      (sd) => sd === SpecialDetail.DeniedPurchase
    ),
    ConvictedOrFinedOrPenalised: getSpecialDetails(funnelState).find(
      (sd) => sd === SpecialDetail.ConvictedOrFinedOrPenalised
    ),
  };

  return (
    <>
      <SummaryCard
        heading="List of additional drivers"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.AdditionalDriversAndUnder25,
            MotorQuoteSubSection.AdditionalDrivers
          )
        }
      >
        {additionalDrivers.length > 0 ? (
          additionalDrivers.map((ad) => (
            <Text key={`${ad.firstName}-${ad.lastName}`}>
              {`${ad.firstName} ${ad.lastName}`}
              <br />
              {`${ad.gender} - ${toAussieStandardDateFormat(ad.dob)}`}
              <br />
              {`Licence held for ${ad.yearsDriving} year${
                ad.yearsDriving === 1 ? '' : 's'
              }`}
              <br />
              {`${ad.claimsMade} Claim${
                ad.claimsMade === 1 ? '' : 's'
              } in last 3 years`}
              <br />
              {`${
                ad.payableClaims >= 0 ? ad.payableClaims : 0
              } Excess payable claim${ad.payableClaims === 1 ? '' : 's'}`}
            </Text>
          ))
        ) : (
          <Text>None</Text>
        )}
      </SummaryCard>
      {isComprehensiveSelected(funnelState) && (
        <SummaryCard
          heading="Under 25 cover"
          onEditClick={
            isCoverUnder25Included(funnelState)
              ? undefined
              : () =>
                  onEditQuoteDetails(
                    MotorSection.AdditionalDriversAndUnder25,
                    MotorQuoteSubSection.CoverForDriversUnder25
                  )
          }
        >
          <Text>{isUnder25CoverSelected(funnelState) ? 'Yes' : 'No'}</Text>
        </SummaryCard>
      )}
      <SummaryCard
        heading="Drivers' declaration"
        onEditClick={() =>
          onEditPolicyDetails(
            MotorSection.AdditionalDriversAndUnder25,
            MotorPolicySubSection.SpecialDetails
          )
        }
      >
        <List bullet>
          <ListItem>
            <Text>
              {driverSpecialDetail.LicenceCancelled
                ? 'Had a licence cancelled, suspended, disqualified, or any special conditions imposed '
                : 'No licence cancelled, suspended, disqualified, or any special conditions imposed'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {driverSpecialDetail.InsuranceClaimRefused
                ? 'Had insurance claim refused'
                : 'No insurance claim refused'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {driverSpecialDetail.InsurancePolicyCancelled
                ? 'Had an insurance policy cancelled'
                : 'No insurance policy cancelled by insurer'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {driverSpecialDetail.DeniedPurchase
                ? 'Have been denied purchase of new or renewed insurance'
                : 'Have not been denied purchase of new or renewed insurance'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {driverSpecialDetail.ConvictedOrFinedOrPenalised
                ? 'Have been convicted, fined or penalised for any alcohol or drug related driving offence'
                : 'Have not been convicted, fined or penalised for any alcohol or drug related driving offence'}
            </Text>
          </ListItem>
        </List>
      </SummaryCard>
    </>
  );
};

export default AdditionalDriverAndUnder25Summary;
